import { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import classes from "../../styles/DashboardView.module.css";
import { CategoryRevenueCompare } from "../../utilities/dashboardAggregations";
import { numberWithCommas } from "../Oversigt";
import { YoYLegend, lightenMetricColor } from "./YoYLegend";
import { IndexBadge } from "./IndexBadge";

interface CategoryRevenueChartProps {
    data: CategoryRevenueCompare[];
    currentYearLabel: string;
    prevYearLabel: string;
}

const COLOR = "#2e7d32";
const PREV_COLOR = lightenMetricColor(COLOR);

export function CategoryRevenueChart({ data, currentYearLabel, prevYearLabel }: CategoryRevenueChartProps) {
    const [dialogData, setDialogData] = useState<CategoryRevenueCompare | null>(null);

    if (!data || data.length === 0) {
        return (
            <Paper className={classes.chartCard} elevation={2}>
                <div className={classes.chartTitle}>Omsætning pr. kategori</div>
                <p>Ingen data</p>
            </Paper>
        );
    }

    const handleBarClick = (barData: any) => {
        if (barData && barData.activePayload && barData.activePayload[0]) {
            setDialogData(barData.activePayload[0].payload);
        }
    };

    return (
        <Paper className={classes.chartCard} elevation={2}>
            <div className={classes.chartHeader}>
                <h3 className={classes.chartTitle}>
                    Omsætning pr. kategori <span style={{ fontSize: 11, color: "#8a8f96", fontWeight: 400 }}>(klik for detaljer)</span>
                </h3>
                <YoYLegend currentColor={COLOR} prevColor={PREV_COLOR} currentYearLabel={currentYearLabel} prevYearLabel={prevYearLabel} />
            </div>
            <ResponsiveContainer width="100%" height={520}>
                <BarChart data={data} layout="vertical" margin={{ left: 20 }} onClick={handleBarClick} style={{ cursor: "pointer" }} barCategoryGap="22%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `${numberWithCommas(v)}`} />
                    <YAxis type="category" dataKey="category" width={130} tick={{ fontSize: 12 }} interval={0} />
                    <Tooltip content={({ active, payload }: any) => {
                        if (!active || !payload || !payload[0]) return null;
                        const entry: CategoryRevenueCompare = payload[0].payload;
                        return (
                            <div style={{ background: "white", border: "1px solid #ccc", padding: 10, borderRadius: 4, maxWidth: 320 }}>
                                <div style={{ fontWeight: 600, marginBottom: 6 }}>{entry.category}</div>
                                <div style={{ color: COLOR, fontWeight: 600 }}>{currentYearLabel}: {numberWithCommas(entry.current)} kr.</div>
                                <div style={{ color: PREV_COLOR, fontSize: 12, fontWeight: 600 }}>{prevYearLabel}: {numberWithCommas(entry.previous)} kr.</div>
                                <IndexBadge current={entry.current} previous={entry.previous} />
                                {entry.courses && entry.courses.length > 0 && (
                                    <div style={{ marginTop: 6, fontSize: 11, color: "#666", borderTop: "1px solid #eee", paddingTop: 6 }}>
                                        {entry.courses.length <= 8
                                            ? entry.courses.map((c, i) => <div key={i}>- {c}</div>)
                                            : (<>
                                                {entry.courses.slice(0, 5).map((c, i) => <div key={i}>- {c}</div>)}
                                                <div style={{ marginTop: 4, color: COLOR, fontWeight: 500 }}>Klik for at se alle {entry.courses.length} kurser</div>
                                            </>)
                                        }
                                    </div>
                                )}
                            </div>
                        );
                    }} />
                    <Bar dataKey="previous" fill={PREV_COLOR} radius={[0, 3, 3, 0]} />
                    <Bar dataKey="current" fill={COLOR} radius={[0, 3, 3, 0]} />
                </BarChart>
            </ResponsiveContainer>

            <Dialog open={dialogData !== null} onClose={() => setDialogData(null)} maxWidth="xs" fullWidth>
                {dialogData && (
                    <>
                        <DialogTitle sx={{ pb: 0.5 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontSize: 16, fontWeight: 600 }}>{dialogData.category}</span>
                                <IconButton onClick={() => setDialogData(null)} size="small"><CloseIcon fontSize="small" /></IconButton>
                            </div>
                            <div style={{ fontSize: 13, color: COLOR, fontWeight: 500 }}>
                                {currentYearLabel}: {numberWithCommas(dialogData.current)} kr. · {prevYearLabel}: {numberWithCommas(dialogData.previous)} kr.
                            </div>
                        </DialogTitle>
                        <DialogContent sx={{ pt: 1 }}>
                            <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>{dialogData.courses ? dialogData.courses.length : 0} kurser</div>
                            {dialogData.courses && dialogData.courses.map((course, i) => (
                                <div key={i} style={{ padding: "6px 0", borderBottom: i < (dialogData.courses?.length ?? 0) - 1 ? "1px solid #f0f0f0" : "none", fontSize: 13 }}>{course}</div>
                            ))}
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </Paper>
    );
}
